import type { OperationNodeSpec } from '../../graph/domain/types';
import type { WorkflowGraph } from '../../graph';
import type { Store } from '../types';
import { generateGuid } from '../../graph/utils/id';
import { withSyncGuard } from '../sync/syncFlag';

type SetState = (partial: Partial<Store> | ((state: Store) => Partial<Store>)) => void;
type GetState = () => Store;

export function createOperationActions(
  set: SetState,
  get: GetState,
  getGraph: () => WorkflowGraph | null | undefined,
) {
  return {
    addOperationNode(spec: OperationNodeSpec): string {
      const id = spec.id ?? generateGuid();
      const fullSpec: OperationNodeSpec = {
        ...spec,
        id,
        vertexId: spec.vertexId ?? id,
        createdAt: spec.createdAt ?? new Date().toISOString(),
      };

      set((state) => ({
        graphData: {
          ...state.graphData,
          operationNodes: { ...state.graphData.operationNodes, [id]: fullSpec },
        },
        ui: { ...state.ui, isDirty: true },
      }));

      // Only COMPLEX operations are rendered as maxGraph vertices.
      // TRANSFORM / TRANSFER / MEASURE are projected as edges by useVisualGraphSync.
      if (fullSpec.archetype === 'COMPLEX') {
        withSyncGuard(set, () => {
          getGraph()?.addOperationNode(fullSpec);
        });
      }

      return id;
    },

    updateOperationNode(id: string, patch: Partial<OperationNodeSpec>): void {
      const current = get().graphData.operationNodes[id];
      if (!current) throw new Error(`OperationNode not found: ${id}`);

      const updated = { ...current, ...patch };

      set((state) => ({
        graphData: {
          ...state.graphData,
          operationNodes: { ...state.graphData.operationNodes, [id]: updated },
        },
        ui: { ...state.ui, isDirty: true },
      }));

      if (updated.archetype === 'COMPLEX') {
        withSyncGuard(set, () => {
          getGraph()?.updateOperationNode(id, patch);
        });
      }
    },

    removeOperationNode(id: string): void {
      const current = get().graphData.operationNodes[id];

      set((state) => {
        const { [id]: _, ...rest } = state.graphData.operationNodes;
        const flowEdges: Record<string, typeof state.graphData.flowEdges[string]> = {};
        for (const [eid, edge] of Object.entries(state.graphData.flowEdges)) {
          if (edge.sourceId !== id && edge.targetId !== id) {
            flowEdges[eid] = edge;
          }
        }
        return {
          graphData: { ...state.graphData, operationNodes: rest, flowEdges },
          ui: { ...state.ui, isDirty: true },
        };
      });

      if (current?.archetype === 'COMPLEX') {
        withSyncGuard(set, () => {
          getGraph()?.removeOperationNode(id);
        });
      }
    },
  };
}
